import { FC, useCallback } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { Addon, CmsProduct, Language, RichText, ShopContent } from '../../../types/cms_shared_types';
import { Step } from '../Step';
import {
  displayProductInformation,
  retrieveCmsProduct,
  findSelectedCategoryByKey,
  findSelectedSubcategoryByKey,
  displaySchablonProductInformation,
} from '../../../helper/cms';
import { ContactInformation } from './ContactInformation';
import { Conditional } from '../../Common/Conditional';
import { LineDelimiter } from '../../Common/LineDelimiter';
import { UserBooleanInput } from '../../Common/UserBooleanInput';
import { SignBottomBar } from '../BottomBar/SignBottomBar';
import {
  ContactFormInputs,
  InformationFormInputs,
  InvoiceFormInputs,
  InvoiceMethod,
  ShopForm,
} from '../../../types/api_shared_types';
import { Product } from '../../../types/webshop';
import { parseAndFormatPhoneNumber, validatePhoneNumber } from '../../../helper/phone';
import { EMAIL_REGEX, REFERENCE_NUMBER_REGEX } from '../../../helper/helper';
import { validateCharacters } from '../../../helper/validation';
import { TermsAndConditions } from './TermsAndConditions';
import { FormikPersist } from '../../Common/FormikPersist';

interface ContactProps {
  selectedProducts: Product[];
  selectedAddons: Addon[];
  selectedCategoryKey?: string;
  selectedSubcategoryKey?: string;
  comments?: string;
  informationFormInputs: InformationFormInputs;
  invoiceFormInputs: InvoiceFormInputs;
  contactFormInputs: ContactFormInputs;
  setContactFormInputs: (inputs: ContactFormInputs) => void;
  startOneflowAgreementSigning: (shopForm: ShopForm) => Promise<void>;
  atLeastOneReportingProduct: boolean;
  cmsShopContent: ShopContent;
  referenceNumber: string | null;
  language: Language;
  registerOnBack?: (callback: () => void) => void;
}

export const Contact: FC<ContactProps> = ({
  selectedProducts,
  selectedAddons,
  selectedCategoryKey,
  selectedSubcategoryKey,
  comments,
  informationFormInputs,
  invoiceFormInputs,
  contactFormInputs,
  setContactFormInputs,
  startOneflowAgreementSigning,
  atLeastOneReportingProduct,
  cmsShopContent,
  referenceNumber,
  language,
  registerOnBack,
}) => {
  const soundtrackAddonSelected = useCallback((): boolean => {
    const addonProviderUuids = selectedAddons.map((addon) => addon.addonProvider.uuid);
    return addonProviderUuids.includes(cmsShopContent.contactStep.soundtrackProvider.uuid);
  }, [selectedAddons, cmsShopContent.contactStep.soundtrackProvider.uuid]);

  const hasExtraProductsSelected = (): boolean => {
    if (!selectedCategoryKey || !selectedSubcategoryKey) {
      return false;
    }

    const category = findSelectedCategoryByKey(cmsShopContent.categories, selectedCategoryKey);
    if (category) {
      const subcategory = findSelectedSubcategoryByKey(selectedSubcategoryKey, category.subcategories);
      if (subcategory) {
        const extraProducts = new Set(subcategory.extraProducts.map((p) => p.code));
        return selectedProducts.some((p) => extraProducts.has(p.code));
      }
    }
    return false;
  };

  const getSummary = (prod: Product, product?: CmsProduct): string => {
    if (!product) {
      return `${prod.quantity}, ${prod.timeQuantity}`;
    }

    if (product.isSchablon) {
      return displaySchablonProductInformation(prod, product);
    }

    if (product.reporting) {
      return cmsShopContent.reportingProductSummary;
    }

    return displayProductInformation(product, selectedProducts);
  };

  const getSoundtrackEmail = useCallback(
    (values: ContactFormInputs): string | undefined => {
      if (!soundtrackAddonSelected()) {
        return undefined;
      }

      return values.sameSoundtrackEmailInput ? values.contactEmailInput : values.soundtrackEmailInput;
    },
    [soundtrackAddonSelected],
  );

  const getAdditionalConditionsDescription = (): RichText | undefined => {
    if (!selectedCategoryKey || !selectedSubcategoryKey) {
      return undefined;
    }
    const category = findSelectedCategoryByKey(cmsShopContent.categories, selectedCategoryKey);
    if (category) {
      const subcategory = findSelectedSubcategoryByKey(selectedSubcategoryKey, category.subcategories);
      return subcategory?.additionalConditionsDescription;
    }
    return undefined;
  };

  const additionalConditionsDesc = getAdditionalConditionsDescription();

  const validationSchema = Yup.object().shape({
    contactFirstNameInput: Yup.string()
      .required(cmsShopContent.inputRequiredError)
      .test('firstName', cmsShopContent.invalidCharacterError, (v) => validateCharacters(v ?? '')),
    contactLastNameInput: Yup.string()
      .required(cmsShopContent.inputRequiredError)
      .test('lastName', cmsShopContent.invalidCharacterError, (v) => validateCharacters(v ?? '')),
    contactEmailInput: Yup.string()
      .matches(EMAIL_REGEX, cmsShopContent.invalidEmailError)
      .required(cmsShopContent.inputRequiredError),
    contactTelephoneNumberInput: Yup.string()
      .required(cmsShopContent.inputRequiredError)
      .test('phoneNumber', cmsShopContent.invalidPhoneNumberError, validatePhoneNumber),
    sameReportingContactInput: Yup.boolean(),
    sameSoundtrackEmailInput: Yup.boolean(),
    soundtrackEmailInput: Yup.string().when('sameSoundtrackEmailInput', {
      is: true,
      then: () => Yup.string().notRequired(),
      otherwise: () =>
        Yup.string().required(cmsShopContent.inputRequiredError).matches(EMAIL_REGEX, cmsShopContent.invalidEmailError),
    }),
    reportingFirstNameInput: Yup.string().when('sameReportingContactInput', {
      is: true,
      then: () => Yup.string().notRequired(),
      otherwise: () => Yup.string().required(cmsShopContent.inputRequiredError),
    }),
    reportingLastNameInput: Yup.string().when('sameReportingContactInput', {
      is: true,
      then: () => Yup.string().notRequired(),
      otherwise: () => Yup.string().required(cmsShopContent.inputRequiredError),
    }),
    reportingEmailInput: Yup.string().when('sameReportingContactInput', {
      is: true,
      then: () => Yup.string().notRequired(),
      otherwise: () =>
        Yup.string().matches(EMAIL_REGEX, cmsShopContent.invalidEmailError).required(cmsShopContent.inputRequiredError),
    }),
    reportingTelephoneNumberInput: Yup.string()
      .notRequired()
      .when('sameReportingContactInput', {
        is: false,
        then: (schema) =>
          schema.when('reportingTelephoneNumberInput', {
            is: (value: string) => value?.length,
            then: (schema) => schema.test('phoneNumber', cmsShopContent.invalidPhoneNumberError, validatePhoneNumber),
          }),
      }),

    acceptTermsAndConditions: Yup.boolean().isTrue().required(),
  });

  return (
    <Formik
      onSubmit={async (values) => {
        const shopForm: ShopForm = {
          products: selectedProducts.map((p) => {
            const product =
              selectedCategoryKey && selectedSubcategoryKey
                ? retrieveCmsProduct(p.code, cmsShopContent.categories, selectedCategoryKey, selectedSubcategoryKey)
                : undefined;

            return {
              code: p.code,
              quantity: p.schablonQuantity ?? p.quantity,
              timeQuantity: p.schablonTimeQuantity ?? p.timeQuantity,
              musicClass: p.musicClass,
              startDate: p.startDate,
              endDate: p.endDate,
              priceYearInclVat: product?.reporting ? 0 : p.priceYearInclVat,
              priceYearExclVat: product?.reporting ? 0 : p.priceYearExclVat,
              name: product?.name ?? '',
              summary: getSummary(p, product),
              reporting: product?.reporting ?? false,
              triggerDiscount: product?.triggerDiscount,
              discountReplacementProduct: product?.discountReplacementProduct,
            };
          }),

          addons: selectedAddons.map((addon) => {
            const soundtrackDescription = `${cmsShopContent.contactStep.soundtrackAccountTitle}: ${
              values.sameSoundtrackEmailInput ? values.contactEmailInput : values.soundtrackEmailInput
            }`;
            const description =
              addon.addonProvider.uuid === cmsShopContent.contactStep.soundtrackProvider.uuid
                ? soundtrackDescription
                : '';

            return {
              code: addon.code,
              name: addon.name,
              description: description,
              forcesMonthlyInvoicing: addon.addonProvider.forceMonthlyInvoicing,
              monthlyPriceExclVat: addon.monthlyPriceExclVat,
              monthlyPriceInclVat: addon.monthlyPriceInclVat,
              providerIdentifier: addon.addonProvider.identifier,
              providerRequiredData: addon.providerRequiredData,
            };
          }),

          category: selectedCategoryKey ?? '',
          subcategory: selectedSubcategoryKey ?? '',
          comments: comments ?? '',
          information: { ...informationFormInputs },
          invoice: {
            ...invoiceFormInputs,
            organisationNumberInput: invoiceFormInputs.organisationNumberInput.includes('-')
              ? invoiceFormInputs.organisationNumberInput
              : invoiceFormInputs.organisationNumberInput.slice(0, 6) +
                '-' +
                invoiceFormInputs.organisationNumberInput.slice(6),
            startDateInput: dayjs(invoiceFormInputs.startDateInput).format('YYYY-MM-DD'),
            emailAddressPdfInput:
              invoiceFormInputs.selectedInvoiceMethod === InvoiceMethod.EMAIL
                ? invoiceFormInputs.emailAddressPdfInput
                : '',
          },
          contacts: { ...values },
          reviewRequired: hasExtraProductsSelected(),
          referenceNumber: REFERENCE_NUMBER_REGEX.test('' + referenceNumber) ? referenceNumber : undefined,
          soundtrackEmail: getSoundtrackEmail(values),
          language: language,
        };

        shopForm.contacts.reportingTelephoneNumberInput = parseAndFormatPhoneNumber(
          shopForm.contacts.reportingTelephoneNumberInput,
        );
        shopForm.contacts.contactTelephoneNumberInput = parseAndFormatPhoneNumber(
          shopForm.contacts.contactTelephoneNumberInput,
        );
        await startOneflowAgreementSigning(shopForm);

        setContactFormInputs(values);
      }}
      initialValues={{
        ...contactFormInputs,
        sameSoundtrackEmailInput: true,
      }}
      validationSchema={validationSchema}
      validateOnMount
    >
      {({ isValid, values, setFieldValue, submitForm, isSubmitting }) => (
        <>
          <FormikPersist<ContactFormInputs> registerOnBack={registerOnBack} onSave={setContactFormInputs} />
          <Step
            title={cmsShopContent.contactStep.title}
            subtitle={cmsShopContent.contactStep.subtitle}
            content={
              <div className="flex flex-col gap-2">
                <ContactInformation
                  cmsShopContent={cmsShopContent}
                  soundtrackAddonSelected={soundtrackAddonSelected()}
                  formikValues={values}
                />

                <Conditional
                  condition={atLeastOneReportingProduct}
                  trueRender={
                    <UserBooleanInput
                      name="sameReportingContactInput"
                      title={cmsShopContent.contactStep.accountingContactTitle}
                      description={cmsShopContent.contactStep.accountingContactDescription}
                    />
                  }
                />

                <LineDelimiter />

                <Conditional
                  condition={!values.sameReportingContactInput}
                  trueRender={
                    <div>
                      <div className="mb-8 text-xl font-bold text-text-dark">
                        {cmsShopContent.contactStep.accountingContactTitle}
                      </div>
                      <ContactInformation
                        isReportingContact
                        cmsShopContent={cmsShopContent}
                        soundtrackAddonSelected={soundtrackAddonSelected()}
                        formikValues={values}
                      />
                      <LineDelimiter />
                    </div>
                  }
                />

                <div className="mb-8">
                  <div className="mb-2 text-sm font-bold text-text-dark">
                    {cmsShopContent.contactStep.agreementConditionsTitle}
                  </div>
                  <TermsAndConditions
                    selectedAddons={selectedAddons}
                    agreementConditionsDescription={cmsShopContent.contactStep.agreementConditionsDescription}
                    addonConditionsDescriptions={cmsShopContent.contactStep.addonConditionsDescriptions}
                    setFieldValue={setFieldValue}
                    additionalConditionsDescription={additionalConditionsDesc}
                    initialTermsAccepted={contactFormInputs.acceptTermsAndConditions}
                  />
                </div>
              </div>
            }
            bottomBar={
              <SignBottomBar
                continueButtonDisabled={!isValid}
                onClick={submitForm}
                cmsShopContent={cmsShopContent}
                loading={isSubmitting}
              />
            }
          />
        </>
      )}
    </Formik>
  );
};
